import { Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, IconButton, InputAdornment, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TextField, Typography } from '@mui/material'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import GroupsIcon from '@mui/icons-material/Groups'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SearchIcon from '@mui/icons-material/Search'
import { useCallback, useEffect, useState } from 'react'
import { apiDelete, apiGet, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import EditTemplateDialog from '../widgetTemplates/EditTemplateDialog'
import PreviewTemplateDialog from '../widgetTemplates/PreviewTemplateDialog'
import CreateInitialScreenTemplateDialog from './CreateInitialScreenTemplateDialog'
import UserScreenPreviewDialog from './UserScreenPreviewDialog'
import EditAndApplyUserScreenDialog from './EditAndApplyUserScreenDialog'

const ENDPOINT_BASE = 'widgets/initialScreenTemplates'

const userFullName = (target) => [target.name, target.surname].filter(Boolean).join(' ')
const userLabel = (target) => target.username + (userFullName(target) ? ' — ' + userFullName(target) : '')

/**
 * Initial-screen template catalog for a user, opened from the Users table row
 * option "Initial screen templates". Strictly separate from the device template
 * catalog. Covers catalog management (create a new template, snapshot the user's
 * current initial screen via from-user, edit a template, delete it) plus
 * applying a template to the user's initial screen. The user's current layout is
 * only readable for the admin preview (user-screen endpoint), so applying ALWAYS
 * asks for confirmation. The from-user snapshot is reached through that preview:
 * the admin sees the screen first and names the template from there. "Edit and
 * apply" opens a one-off editable copy of a template and applies the result to
 * the user (POST user-screen) without touching the template catalog.
 * A template can also be applied in bulk to every active user sharing the target
 * user's profile: an irreversible-action warning shows the affected count and
 * offers cancelling, excluding users through a checklist, or applying to all.
 */
const UserInitialScreenTemplateDialog = ({ open, setOpen, user, onApplied }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const [AlertElement, showAlert] = AlertUI()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  // { type: 'apply' | 'delete', template } — drives the confirmation dialog.
  const [pendingAction, setPendingAction] = useState(null)
  const [templateToEdit, setTemplateToEdit] = useState(null)
  // "Edit and apply": one-off edit of a template applied straight to the user.
  const [templateToEditApply, setTemplateToEditApply] = useState(null)
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  // Name prompt for the from-user snapshot; kept open on error (e.g. 409 duplicate).
  const [snapshotOpen, setSnapshotOpen] = useState(false)
  const [snapshotName, setSnapshotName] = useState('')
  // Full-screen preview of the user's current screen, the entry point to the snapshot.
  const [userScreenPreviewOpen, setUserScreenPreviewOpen] = useState(false)
  // Bulk apply to every user sharing the target user's profile: the chosen template, the
  // candidate users, the irreversible-action confirmation and the exclusion checklist.
  const [bulkTemplate, setBulkTemplate] = useState(null)
  const [profileUsers, setProfileUsers] = useState([])
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)
  const [excludeOpen, setExcludeOpen] = useState(false)
  const [selectedUserIds, setSelectedUserIds] = useState([])
  const [excludeFilter, setExcludeFilter] = useState('')
  // Per-row overflow menu (preview / edit template / delete).
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [menuTemplate, setMenuTemplate] = useState(null)

  const openRowMenu = (event, template) => {
    setMenuAnchorEl(event.currentTarget)
    setMenuTemplate(template)
  }
  const closeRowMenu = () => {
    setMenuAnchorEl(null)
    setMenuTemplate(null)
  }

  const loadTemplates = useCallback(async () => {
    setLoading(true)
    try {
      const result = await apiGet(ENDPOINT_BASE)
      setTemplates(Array.isArray(result) ? result : [])
    } catch (e) {
      showAlert({ title: t_('error loading initial screen templates'), message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [showAlert, t_])

  useEffect(() => {
    if (open) {
      setSnapshotName('')
      setUserScreenPreviewOpen(false)
      loadTemplates()
    }
  }, [open, loadTemplates])

  const doApply = useCallback(async (template) => {
    if (!template || !user) return
    setBusy(true)
    try {
      await apiPost(ENDPOINT_BASE + '/' + template.id + '/apply/' + user.id, {})
      if (onApplied) onApplied()
      setOpen(false)
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }, [user, onApplied, setOpen, showAlert, t_])

  const doDelete = useCallback(async (template) => {
    if (!template) return
    setBusy(true)
    try {
      await apiDelete(ENDPOINT_BASE + '/' + template.id)
      showAlert({ title: t_('Template deleted'), message: '', severity: 'success' })
      await loadTemplates()
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }, [loadTemplates, showAlert, t_])

  // The target user's current layout is not shown at this point (it is only
  // readable in the user-screen preview), so the overwrite confirmation is
  // unconditional, unlike the device flow.
  const requestApply = useCallback((template) => {
    if (!user) return
    setPendingAction({ type: 'apply', template })
  }, [user])

  const resetBulkFlow = useCallback(() => {
    setBulkTemplate(null)
    setProfileUsers([])
    setBulkConfirmOpen(false)
    setExcludeOpen(false)
    setSelectedUserIds([])
    setExcludeFilter('')
  }, [])

  const requestBulkApply = useCallback(async (template) => {
    if (!user) return
    setBusy(true)
    try {
      const targets = await apiGet(ENDPOINT_BASE + '/profile-users/' + user.id)
      const list = Array.isArray(targets) ? targets : []
      if (list.length === 0) {
        showAlert({ title: t_('No users found with this profile'), message: '', severity: 'warning' })
        return
      }
      setBulkTemplate(template)
      setProfileUsers(list)
      setBulkConfirmOpen(true)
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }, [user, showAlert, t_])

  const doBulkApply = useCallback(async (userIds) => {
    if (!bulkTemplate || userIds.length === 0) return
    setBusy(true)
    try {
      await apiPost(ENDPOINT_BASE + '/' + bulkTemplate.id + '/apply-to-users', { userIds })
      resetBulkFlow()
      if (onApplied) onApplied()
      setOpen(false)
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }, [bulkTemplate, onApplied, resetBulkFlow, setOpen, showAlert, t_])

  const toggleSelectedUser = useCallback((userId) => {
    setSelectedUserIds((prev) => prev.includes(userId)
      ? prev.filter((id) => id !== userId)
      : [...prev, userId])
  }, [])

  // Search and select-all operate on the filtered subset so the admin can
  // exclude e.g. a whole team by name without losing the rest of the selection.
  const normalizedFilter = excludeFilter.trim().toLowerCase()
  const filteredUsers = normalizedFilter
    ? profileUsers.filter((target) => userLabel(target).toLowerCase().includes(normalizedFilter))
    : profileUsers
  const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every((target) => selectedUserIds.includes(target.id))
  const someFilteredSelected = filteredUsers.some((target) => selectedUserIds.includes(target.id))

  const toggleAllFiltered = useCallback(() => {
    const filteredIds = filteredUsers.map((target) => target.id)
    setSelectedUserIds((prev) => filteredIds.every((id) => prev.includes(id))
      ? prev.filter((id) => !filteredIds.includes(id))
      : [...prev, ...filteredIds.filter((id) => !prev.includes(id))])
  }, [filteredUsers])

  const doSnapshot = useCallback(async () => {
    const name = snapshotName.trim()
    if (!name) {
      showAlert({ title: t_('A name is required'), message: '', severity: 'error' })
      return
    }
    setBusy(true)
    try {
      await apiPost(ENDPOINT_BASE + '/from-user/' + user.id, { name })
      showAlert({ title: t_('Template created'), message: '', severity: 'success' })
      setSnapshotOpen(false)
      setSnapshotName('')
      // Back to the catalog so the new template (and the success alert) are visible.
      setUserScreenPreviewOpen(false)
      await loadTemplates()
    } catch (e) {
      // Keep the prompt open so the user can fix the name (e.g. duplicate).
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }, [snapshotName, user, loadTemplates, showAlert, t_])

  const confirmPending = () => {
    const action = pendingAction
    setPendingAction(null)
    if (!action) return
    if (action.type === 'apply') doApply(action.template)
    else doDelete(action.template)
  }

  const confirmMessage = pendingAction?.type === 'delete'
    ? t_('This template will be permanently deleted. Continue?')
    : t_('Applying this template will overwrite the initial screen of this user. Continue?')

  return (
    <>
      <SmartDialog
        setOpen={open}
        closeCallback={() => setOpen(false)}
        close
        accept={false}
        cancel={false}
        paperWidth='min(72rem, 92vw)'
        title={{ icon: null, render: <Typography variant='h5'>{t_('Initial screen templates')}{user ? ' — ' + user.username : ''}</Typography> }}
        renderComponent={
          <Box sx={{ padding: '1rem', width: '100%', boxSizing: 'border-box' }}>
            {AlertElement}
            <Stack direction='row' justifyContent='flex-end' spacing={1} sx={{ mb: 1 }}>
              <Button variant='text' startIcon={<VisibilityIcon />} onClick={() => setUserScreenPreviewOpen(true)} disabled={busy || !user}>
                {t_('Preview user screen')}
              </Button>
              <Button variant='outlined' startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} disabled={busy}>
                {t_('Create new template')}
              </Button>
            </Stack>
            {loading
              ? <Stack alignItems='center' sx={{ py: 3 }}><CircularProgress /></Stack>
              : templates.length === 0
                ? <Typography color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>{t_('No templates yet')}</Typography>
                : (
                  <Stack divider={<Divider flexItem />} spacing={0}>
                    {templates.map((template) => (
                      <Stack key={template.id} direction='row' spacing={0.5} alignItems='center' sx={{ py: 1 }}>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography noWrap>{template.name}</Typography>
                          {template.updatedAt && (
                            <Typography variant='caption' color='text.secondary'>{formatDateTime(template.updatedAt)}</Typography>
                          )}
                        </Box>
                        <Button size='small' variant='outlined' startIcon={<PlaylistAddCheckIcon />} onClick={() => requestApply(template)} disabled={busy}>
                          {t_('Apply')}
                        </Button>
                        <Button size='small' variant='outlined' startIcon={<EditIcon />} onClick={() => setTemplateToEditApply(template)} disabled={busy || !user}>
                          {t_('Edit and apply')}
                        </Button>
                        <IconButton size='small' onClick={(event) => openRowMenu(event, template)} disabled={busy} aria-label={t_('More options')}>
                          <MoreVertIcon fontSize='small' />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                  )}
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeRowMenu}>
              <MenuItem onClick={() => { const template = menuTemplate; closeRowMenu(); setPreviewTemplate(template) }}>
                <ListItemIcon><VisibilityIcon fontSize='small' /></ListItemIcon>
                <ListItemText>{t_('Preview')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { const template = menuTemplate; closeRowMenu(); requestBulkApply(template) }}>
                <ListItemIcon><GroupsIcon fontSize='small' /></ListItemIcon>
                <ListItemText>{t_('Apply to all users with this profile')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { const template = menuTemplate; closeRowMenu(); setTemplateToEdit(template) }}>
                <ListItemIcon><EditIcon fontSize='small' /></ListItemIcon>
                <ListItemText>{t_('Edit template')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { const template = menuTemplate; closeRowMenu(); setPendingAction({ type: 'delete', template }) }}>
                <ListItemIcon><DeleteOutlineIcon fontSize='small' color='error' /></ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>{t_('Delete')}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        }
      />

      <SmartDialog
        setOpen={Boolean(pendingAction)}
        title={createDialogTitle({ color: 'warning', title: t_('Attention'), icon: WarningAmberIcon })}
        message={confirmMessage}
        accept
        acceptLabel={t_('Continue')}
        acceptCallback={confirmPending}
        cancel
        cancelCallback={() => setPendingAction(null)}
      />

      <SmartDialog
        setOpen={bulkConfirmOpen}
        title={createDialogTitle({ color: 'warning', title: t_('Attention'), icon: WarningAmberIcon })}
        message={t_('This action will irreversibly overwrite the initial screen of {{count}} users with the profile {{profile}}. Continue?', { count: profileUsers.length, profile: user?.profile?.name })}
        accept={false}
        cancel
        cancelCallback={resetBulkFlow}
        renderCustomButtons={
          <Stack direction='row' spacing={1}>
            <Button
              variant='outlined'
              onClick={() => {
                setBulkConfirmOpen(false)
                setSelectedUserIds(profileUsers.map((target) => target.id))
                setExcludeFilter('')
                setExcludeOpen(true)
              }}
              disabled={busy}
            >
              {t_('Exclude users')}
            </Button>
            <Button
              variant='contained'
              onClick={() => doBulkApply(profileUsers.map((target) => target.id))}
              disabled={busy}
            >
              {t_('Apply to all')}
            </Button>
          </Stack>
        }
      />

      <SmartDialog
        setOpen={excludeOpen}
        title={createDialogTitle({ color: 'warning', title: t_('Exclude users'), icon: GroupsIcon })}
        accept={false}
        cancel
        cancelCallback={resetBulkFlow}
        renderComponent={
          <Box sx={{ padding: '1rem', width: '100%', boxSizing: 'border-box' }}>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              {t_('Uncheck the users whose initial screen must not be changed')}
            </Typography>
            <TextField
              size='small'
              fullWidth
              placeholder={t_('Search users')}
              value={excludeFilter}
              onChange={(event) => setExcludeFilter(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                )
              }}
            />
            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={allFilteredSelected}
                    indeterminate={someFilteredSelected && !allFilteredSelected}
                    onChange={toggleAllFiltered}
                    disabled={filteredUsers.length === 0}
                  />
                }
                label={<Typography variant='body2'>{t_('Select all')}</Typography>}
              />
              <Typography variant='caption' color='text.secondary'>
                {t_('{{count}} of {{total}} selected', { count: selectedUserIds.length, total: profileUsers.length })}
              </Typography>
            </Stack>
            <List dense disablePadding sx={{ maxHeight: '40vh', overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {filteredUsers.length === 0
                ? (
                  <Typography variant='body2' color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>
                    {t_('No users match the search')}
                  </Typography>
                  )
                : filteredUsers.map((target, index) => {
                  const checked = selectedUserIds.includes(target.id)
                  return (
                    <ListItem key={target.id} disablePadding divider={index < filteredUsers.length - 1}>
                      <ListItemButton dense onClick={() => toggleSelectedUser(target.id)}>
                        <ListItemIcon sx={{ minWidth: '2.25rem' }}>
                          <Checkbox
                            edge='start'
                            size='small'
                            checked={checked}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{ 'aria-label': userLabel(target) }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={target.username}
                          secondary={userFullName(target)}
                          primaryTypographyProps={{ color: checked ? 'text.primary' : 'text.disabled' }}
                          secondaryTypographyProps={{ color: checked ? 'text.secondary' : 'text.disabled' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
                })}
            </List>
          </Box>
        }
        renderCustomButtons={
          <Button
            variant='contained'
            onClick={() => doBulkApply(selectedUserIds)}
            disabled={busy || selectedUserIds.length === 0}
          >
            {t_('Apply to selected users') + ' (' + selectedUserIds.length + ')'}
          </Button>
        }
      />

      <SmartDialog
        setOpen={snapshotOpen}
        title={createDialogTitle({ color: 'primary', title: t_("Create template from this user's screen"), icon: SaveIcon })}
        accept
        acceptLabel={t_('Save template')}
        acceptCallback={doSnapshot}
        cancel
        cancelCallback={() => { setSnapshotOpen(false); setSnapshotName('') }}
        renderComponent={
          <Box sx={{ padding: '1rem', width: '100%', boxSizing: 'border-box' }}>
            <TextField
              label={t_('New template name')}
              value={snapshotName}
              onChange={(event) => setSnapshotName(event.target.value)}
              fullWidth
              inputProps={{ maxLength: 255 }}
            />
          </Box>
        }
      />

      <EditTemplateDialog
        open={Boolean(templateToEdit)}
        template={templateToEdit}
        endpointBase={ENDPOINT_BASE}
        showAllWidgets={false}
        onClose={(saved) => {
          setTemplateToEdit(null)
          if (saved) loadTemplates()
        }}
      />

      <PreviewTemplateDialog
        open={Boolean(previewTemplate)}
        template={previewTemplate}
        endpointBase={ENDPOINT_BASE}
        applyEnabled={Boolean(user)}
        onClose={() => setPreviewTemplate(null)}
        onApply={(template) => { setPreviewTemplate(null); requestApply(template) }}
      />

      <UserScreenPreviewDialog
        open={userScreenPreviewOpen}
        user={user}
        onClose={() => setUserScreenPreviewOpen(false)}
        onCreateTemplate={() => { setSnapshotName(''); setSnapshotOpen(true) }}
      />

      <EditAndApplyUserScreenDialog
        open={Boolean(templateToEditApply)}
        template={templateToEditApply}
        user={user}
        onClose={(applied) => {
          setTemplateToEditApply(null)
          if (applied) {
            if (onApplied) onApplied()
            setOpen(false)
          }
        }}
      />

      <CreateInitialScreenTemplateDialog
        open={createOpen}
        user={user}
        onClose={(outcome) => {
          setCreateOpen(false)
          if (outcome === 'applied') {
            if (onApplied) onApplied()
            setOpen(false)
          } else if (outcome === 'saved') {
            loadTemplates()
          }
        }}
      />
    </>
  )
}

export default UserInitialScreenTemplateDialog
